import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { intlShape } from 'react-intl';
import styled, { css } from 'styled-components';

import Shell from '@shared/containers/Shell';
import InfiniteScroll from '@components/InfiniteScroll';
import ReviewsList from '@components/ReviewsList';
import CardShimmer from '@components/ReviewCardShimmer';

import FixedHeader from '@components/FixedHeader';
import ReviewsListShimmer from './components/ReviewListShimmer';
import NoReviews from './components/NoReviews';
import ReviewCard from './components/ReviewCard';

import messages from './messages';
import connect from './connect';

const HEIGHT_FIXED_HEADER = '44px';

const ReviewsContainer = styled.div`
  height: ${({ isDesktopLayout }) => (isDesktopLayout ? '400px' : '100%')};
  overflow: auto;
  background-color: ${({ theme }) => theme.colors.white};
  ${({ isDesktopLayout }) =>
    !isDesktopLayout &&
    css`
      padding-top: ${HEIGHT_FIXED_HEADER};
    `}
`;

class Reviews extends PureComponent {
  state = { hasFetched: false };

  async componentDidMount() {
    const {
      trackShopReviewsLoaded,
      fetchReviews,
      shopId,
      shop,
      isPickup,
    } = this.props;
    await fetchReviews({ shopId, businessType: shop.businessType, isPickup });
    this.setState({ hasFetched: true });
    trackShopReviewsLoaded();
  }

  componentWillUnmount() {
    const { cleanState } = this.props;
    return cleanState();
  }

  getInfiniteScrollConfiguration = () => {
    const {
      isFetchingReviews,
      reviewsCount,
      total,
      shopId,
      fetchReviews,
      shop,
      isPickup,
    } = this.props;

    if (!isFetchingReviews && reviewsCount === 0) return null;

    return {
      fetchMore: () =>
        !isFetchingReviews &&
        fetchReviews({ shopId, businessType: shop.businessType, isPickup }),
      hasMore: total > reviewsCount,
      loader: <CardShimmer key="0" />,
    };
  };

  render() {
    const {
      reviews,
      reviewsCount,
      intl: { formatMessage },
      isDesktopLayout,
      goBack,
    } = this.props;

    const { hasFetched } = this.state;

    const scrollConfig = this.getInfiniteScrollConfiguration();

    const reviewsRef = React.createRef();
    return (
      <Shell
        isModal
        header={
          <FixedHeader
            isModal
            isFixed={!isDesktopLayout}
            label={formatMessage(messages.header)}
            onClose={goBack}
          />
        }
      >
        <ReviewsContainer isDesktopLayout={isDesktopLayout} ref={reviewsRef}>
          <InfiniteScroll
            fetchMore={scrollConfig?.fetchMore}
            hasMore={scrollConfig?.hasMore}
            loader={scrollConfig?.loader}
            enabled={Boolean(scrollConfig)}
            parentRef={isDesktopLayout ? reviewsRef : null}
          >
            {!hasFetched && <ReviewsListShimmer size={6} />}

            {reviewsCount > 0 && (
              <ReviewsList reviews={reviews} card={ReviewCard} />
            )}

            {hasFetched && reviewsCount === 0 && <NoReviews />}
          </InfiniteScroll>
        </ReviewsContainer>
      </Shell>
    );
  }
}

Reviews.propTypes = {
  // data
  shop: PropTypes.object,
  shopId: PropTypes.number.isRequired,
  reviews: PropTypes.array.isRequired,
  reviewsCount: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  isFetchingReviews: PropTypes.bool.isRequired,
  isDesktopLayout: PropTypes.bool.isRequired,
  isPickup: PropTypes.bool.isRequired,
  // callbacks
  fetchReviews: PropTypes.func.isRequired,
  cleanState: PropTypes.func.isRequired,
  intl: intlShape,
  goBack: PropTypes.func.isRequired,

  // tracking
  trackShopReviewsLoaded: PropTypes.func.isRequired,
};

export default connect(Reviews);
